import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import PendingIcon from "@mui/icons-material/Pending";
import LinkIcon from "@mui/icons-material/Link";
import CreateIcon from "@mui/icons-material/Create";
import DescriptionIcon from "@mui/icons-material/Description";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TimerIcon from "@mui/icons-material/Timer";
import GavelIcon from "@mui/icons-material/Gavel";
import SchoolIcon from "@mui/icons-material/School";
import PeopleIcon from "@mui/icons-material/People";
import { ImageBaseURL } from "../../utilities/api/axios";

const progress = {
  1: "Archived",
  2: "In Progress",
  3: "Completed"
};

const type = {
  1: "Academic",
  2: "Research",
  3: "Personal",
  4: "Other"
};

const ProjectViewDrawer = ({ drawerOpen, toggleDrawer, currentProject, getUserDetails }) => {
  const getProfilePicUrl = (path) => path ? `${ImageBaseURL}${path}` : "";

  const creator = getUserDetails(currentProject?.student);
  const teammates = currentProject?.teammates?.map((id) => getUserDetails(id))?.filter(Boolean) || [];

  const InfoRow = ({ icon: Icon, label, value }) => (
    <Box
      sx={{
        p: 2,
        mb: 2,
        backgroundColor: '#f5f5f5',
        borderRadius: 1,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2
      }}
    >
      <Icon sx={{ fontSize: 22, color: '#666', mt: 0.25, flexShrink: 0 }} />
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 14, color: '#888', fontWeight: 500, mb: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontSize: 16, color: '#333', fontWeight: 500 }}>{value || 'N/A'}</Typography>
      </Box>
    </Box>
  );

  return (
    <Drawer anchor="right" open={drawerOpen} onClose={() => toggleDrawer(false)}>
      <Box sx={{ width: '30vw', height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Sticky Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            position: 'sticky',
            top: 0,
            backgroundColor: '#2c2c2c',
            color: 'white',
            p: 2,
            zIndex: 10,
            borderBottom: '1px solid #ddd'
          }}
        >
          <Typography sx={{ fontSize: 20, fontWeight: 600 }}>View Project</Typography>
          <IconButton onClick={() => toggleDrawer(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Scrollable Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2, backgroundColor: '#ffffff' }}>
          <InfoRow icon={NoteAddIcon} label="Type of Project" value={type[currentProject?.type]} />

          {/* Progress Status Toggle */}
          <Box
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2
            }}
          >
            <PendingIcon sx={{ fontSize: 22, color: '#666', mt: 0.25, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 14, color: '#888', fontWeight: 500, mb: 1 }}>Progress Status</Typography>
              <FormControl size="small">
                <RadioGroup
                  row
                  value={String(currentProject?.progress_status) || '1'}
                  sx={{ gap: 2 }}
                >
                  <FormControlLabel
                    value="1"
                    control={<Radio size="small" />}
                    label={<Typography sx={{ fontSize: 14 }}>Archived</Typography>}
                  />
                  <FormControlLabel
                    value="2"
                    control={<Radio size="small" />}
                    label={<Typography sx={{ fontSize: 14 }}>In Progress</Typography>}
                  />
                  <FormControlLabel
                    value="3"
                    control={<Radio size="small" />}
                    label={<Typography sx={{ fontSize: 14 }}>Completed</Typography>}
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          </Box>

          <InfoRow icon={LinkIcon} label="Links/Documents" value={currentProject?.links} />
          <InfoRow icon={CreateIcon} label="Title" value={currentProject?.title} />
          <InfoRow icon={DescriptionIcon} label="Description" value={currentProject?.description} />
          <InfoRow icon={AttachMoneyIcon} label="Budget" value={currentProject?.budget} />
          <InfoRow icon={AttachMoneyIcon} label="Expenditure" value={currentProject?.expenditure} />
          <InfoRow icon={TimerIcon} label="Duration" value={currentProject?.duration} />

          {/* Patent Filed Toggle */}
          <Box
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2
            }}
          >
            <GavelIcon sx={{ fontSize: 22, color: '#666', mt: 0.25, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13, color: '#888', fontWeight: 500, mb: 1 }}>Patent Filed</Typography>
              <FormControl size="small">
                <RadioGroup
                  row
                  value={currentProject?.is_patent_filed ? 'yes' : 'no'}
                  sx={{ gap: 2 }}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio size="small" />}
                    label={<Typography sx={{ fontSize: 13 }}>Yes</Typography>}
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio size="small" />}
                    label={<Typography sx={{ fontSize: 13 }}>No</Typography>}
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          </Box>

          <InfoRow icon={SchoolIcon} label="Mentors" value={currentProject?.mentors} />

          {/* Creator Section */}
          <Box sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <PeopleIcon sx={{ fontSize: 22, color: '#666', flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, color: '#888', fontWeight: 500, mb: 0.5 }}>Creator</Typography>
                {creator && (
                  <Chip
                    avatar={<Avatar src={getProfilePicUrl(creator.profile_pic)} />}
                    label={`${creator.name} (${creator.username})`}
                    size="small"
                  />
                )}
              </Box>
            </Stack>
          </Box>

          {/* Teammates Section */}
          <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <PeopleIcon sx={{ fontSize: 22, color: '#666', mt: 0.25, flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13, color: '#888', fontWeight: 500, mb: 1 }}>Teammates</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                  {teammates.map((t) => (
                    <Chip
                      key={t.id}
                      avatar={<Avatar src={getProfilePicUrl(t.profile_pic)} />}
                      label={`${t.name} (${t.username})`}
                      size="small"
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ProjectViewDrawer;